import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Event as EventIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  VideoCall as VideoCallIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { Interview, Application, Candidate, Position } from '../../types';
import { useNavigate } from 'react-router-dom';
import airtableService from '../../services/airtable';

const InterviewsList: React.FC = () => {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [interviewsResponse, applicationsResponse, candidatesResponse, positionsResponse] = await Promise.all([
        airtableService.getInterviews(),
        airtableService.getApplications(),
        airtableService.getCandidates(),
        airtableService.getPositions()
      ]);

      if (interviewsResponse.success) {
        console.log('📋 Interviews data received:', {
          total: interviewsResponse.data.length,
          sampleInterview: interviewsResponse.data[0] ? {
            id: interviewsResponse.data[0].id,
            interviewerName: interviewsResponse.data[0].interviewerName,
            candidateImage: interviewsResponse.data[0].candidateImage,
            type: interviewsResponse.data[0].type,
            stage: interviewsResponse.data[0].stage
          } : 'No interviews'
        });
        setInterviews(interviewsResponse.data);
      }
      if (applicationsResponse.success) {
        setApplications(applicationsResponse.data);
      }
      if (candidatesResponse.success) {
        setCandidates(candidatesResponse.data);
      }
      if (positionsResponse.success) {
        setPositions(positionsResponse.data);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const getCandidateName = (applicationId: string) => {
    const application = applications.find(app => app.id === applicationId);
    if (!application) return 'Unknown Candidate';

    const candidate = candidates.find(c => c.id === application.candidateId);
    return candidate ? `${candidate.firstName} ${candidate.lastName}` : 'Unknown Candidate';
  };

  const getPositionTitle = (applicationId: string) => {
    const application = applications.find(app => app.id === applicationId);
    if (!application) return 'Unknown Position';

    const position = positions.find(p => p.id === application.positionId);
    return position ? position.title : 'Unknown Position';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Scheduled': return 'primary';
      case 'In Progress': return 'warning';
      case 'Completed': return 'success';
      case 'Cancelled': return 'error';
      case 'Rescheduled': return 'info';
      default: return 'default';
    }
  };


  const filteredInterviews = interviews.filter(interview => {
    const candidateName = getCandidateName(interview.applicationId);
    const positionTitle = getPositionTitle(interview.applicationId);
    const matchesSearch = `${candidateName} ${positionTitle} ${interview.type} ${interview.stage}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || interview.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Group interviews by position
  const groupedInterviews = filteredInterviews.reduce((groups: { [key: string]: Interview[] }, interview) => {
    const positionTitle = getPositionTitle(interview.applicationId);
    if (!groups[positionTitle]) {
      groups[positionTitle] = [];
    }
    groups[positionTitle].push(interview);
    return groups;
  }, {});

  // Sort groups by position title and sort interviews within each group
  const sortedGroups = Object.keys(groupedInterviews)
    .sort()
    .reduce((result: { [key: string]: Interview[] }, positionTitle) => {
      result[positionTitle] = groupedInterviews[positionTitle].sort((a, b) => {
        // Sort by scheduled date, then by candidate name
        const dateA = a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
        const dateB = b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
        if (dateA !== dateB) return dateA - dateB;

        const nameA = getCandidateName(a.applicationId).toLowerCase();
        const nameB = getCandidateName(b.applicationId).toLowerCase();
        return nameA.localeCompare(nameB);
      });
      return result;
    }, {});

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setFilterAnchor(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="h6">
          Unable to load interviews. Please check your Airtable configuration.
        </Typography>
        <Button variant="outlined" onClick={fetchData} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Interviews ({filteredInterviews.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/interviews/new')}
        >
          Schedule Interview
        </Button>
      </Box>

      {/* Search and Filter */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          placeholder="Search interviews..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <IconButton onClick={handleFilterClick}>
          <FilterIcon />
        </IconButton>
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={handleFilterClose}
        >
          <MenuItem onClick={() => handleStatusFilter('')}>All Status</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('Scheduled')}>Scheduled</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('In Progress')}>In Progress</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('Completed')}>Completed</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('Cancelled')}>Cancelled</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('Rescheduled')}>Rescheduled</MenuItem>
        </Menu>
      </Box>

      {/* Grouped Interviews */}
      {Object.keys(sortedGroups).length > 0 ? (
        Object.keys(sortedGroups).map((positionTitle) => (
          <Box key={positionTitle} sx={{ mb: 4 }}>
            {/* Position Group Header */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                  pb: 1,
                  mb: 2
                }}
              >
                {positionTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {sortedGroups[positionTitle].length} interview{sortedGroups[positionTitle].length !== 1 ? 's' : ''}
              </Typography>
            </Box>

            {/* Interviews Grid for this Position */}
            <Grid container spacing={3}>
              {sortedGroups[positionTitle].map((interview) => (
                <Grid item xs={12} md={6} lg={3} key={interview.id}>
                  <Card
                    elevation={2}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { elevation: 4 },
                      transition: 'box-shadow 0.2s',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onClick={() => navigate(`/interviews/${interview.id}`)}
                  >
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* Candidate Name Section - At Top */}
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            mr: 2,
                            bgcolor: 'primary.main'
                          }}
                          src={interview.candidateImage?.[0]?.thumbnails?.small?.url || interview.candidateImage?.[0]?.url}
                        >
                          {!interview.candidateImage?.length && <PersonIcon sx={{ fontSize: 40 }} />}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Candidate
                          </Typography>
                          <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                            {getCandidateName(interview.applicationId)}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Interview Info Section */}
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {interview.type}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {interview.stage}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Chip
                            label={interview.status}
                            color={getStatusColor(interview.status) as any}
                            size="small"
                            sx={{ mb: 1 }}
                          />
                        </Box>
                      </Box>

                      {/* Interviewer Section - Left Aligned */}
                      {interview.interviewerName && (
                        <Box display="flex" alignItems="center" mb={2}>
                          <Typography variant="subtitle1" color="text.secondary" sx={{ mr: 1 }}>
                            Interviewer:
                          </Typography>
                          <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                            {interview.interviewerName}
                          </Typography>
                        </Box>
                      )}

                      {/* Position Section */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Position
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getPositionTitle(interview.applicationId)}
                        </Typography>
                      </Box>

                      {/* Details Section - Fixed Height */}
                      <Box sx={{ flexGrow: 1, minHeight: '120px', mb: 2 }}>
                        {interview.scheduledDate && (
                          <Box display="flex" alignItems="center" mb={1}>
                            <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {interview.scheduledDate.toLocaleDateString('en-US', {
                                timeZone: 'America/Los_Angeles',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })} at {interview.scheduledTime}
                            </Typography>
                          </Box>
                        )}

                        {interview.duration && (
                          <Box display="flex" alignItems="center" mb={1}>
                            <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {interview.duration} minutes
                            </Typography>
                          </Box>
                        )}

                        {interview.meetingLink && (
                          <Box display="flex" alignItems="center" mb={1}>
                            <VideoCallIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              Video call scheduled
                            </Typography>
                          </Box>
                        )}

                        {interview.location && (
                          <Box display="flex" alignItems="center" mb={1}>
                            <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {interview.location}
                            </Typography>
                          </Box>
                        )}

                        {interview.interviewerName && (
                          <Box display="flex" alignItems="center" mb={1}>
                            <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              Interviewer: {interview.interviewerName}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Notes Section - Extended to 10 lines */}
                      <Box sx={{ minHeight: '60px', mb: 2 }}>
                        {interview.interviewNotes && (
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Notes
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 10,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.5,
                            }}>
                              {interview.interviewNotes}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      ) : (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No interviews found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria or schedule a new interview
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default InterviewsList;